import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { Link } from 'react-router-dom';
import { updateUser } from '../store/map3PendingSlice';
import { AddEditDeleteBar1 } from '../components_maps/stable/AddEditDeleteBar1';
import { useAuth } from '../components_auth/AuthContext';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface EditableField {
  key: string;
  label: string;
  value: string;
  type?: 'text' | 'email' | 'tel';
  isArray?: boolean;
}

export function UserProfilePage() {
  const dispatch = useDispatch<AppDispatch>();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const { user: authUser } = useAuth();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [arrayInputs, setArrayInputs] = useState<string[]>([]);
  const [inputErrors, setInputErrors] = useState<string[]>([]);
  const authEmail = authUser?.email || '';

  const fullName =
    [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ') || 'Name not set';

  useDocumentTitle(fullName);

  const getAccountTypeDisplay = (type: string) => {
    switch (type) {
      case 'alpha_member':
        return 'member';
      case 'alpha_guest':
        return 'guest';
      case 'alpha_pending':
        return 'pending';
      default:
        return 'NA';
    }
  };

  const editableFields: EditableField[] = [
    { key: 'uid', label: 'User ID', value: userProfile.uid || '' },
    { key: 'userName', label: 'User Name', value: userProfile.userName || '' },
    { key: 'firstName', label: 'First Name', value: userProfile.firstName || '' },
    { key: 'lastName', label: 'Last Name', value: userProfile.lastName || '' },
    {
      key: 'email',
      label: 'Email',
      value: (userProfile.email || []).join(', '),
      type: 'email',
      isArray: true,
    },
    {
      key: 'phone',
      label: 'Phone',
      value: (userProfile.phone || []).join(', '),
      type: 'tel',
      isArray: true,
    },
    {
      key: 'accountType',
      label: 'Account Type',
      value: getAccountTypeDisplay(userProfile.accountType || ''),
    },
  ];
  const isEditable = (key: string) => {
    return !['uid', 'userName', 'accountType'].includes(key);
  };

  const handleArrayEdit = (key: string, currentValues: string[]) => {
    setEditingField(key);
    setArrayInputs(currentValues.length ? currentValues : ['']);
    setInputErrors([]);
  };

  const handleArraySave = async (key: string) => {
    // Remove empty values and duplicates
    const validInputs = arrayInputs
      .map((input) => input.trim())
      .filter((input, index, self) => input && self.indexOf(input) === index);

    if (key === 'email') {
      // Validate emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const newErrors = arrayInputs.map((input) =>
        input.trim() && !emailRegex.test(input) ? 'Invalid email format' : '',
      );

      if (newErrors.some((error) => error)) {
        setInputErrors(newErrors);
        return;
      }

      // Ensure auth email is preserved
      // TODO: This is a sucky user experience. User should have a way to change how they authenticate.
      if (authUser?.email && !validInputs.includes(authUser.email)) {
        setInputErrors([
          `The email ${authUser.email} is used for authentication and cannot be removed. If you need to change this, please contact Support. Sorry for the inconvenience.`,
        ]);
        return;
      }
    }

    try {
      await dispatch(
        updateUser({
          uid: userProfile.uid,
          updates: { [key]: validInputs },
        }),
      );
      setEditingField(null);
    } catch (error) {
      console.error('Error updating profile:', error);
      setInputErrors(['Failed to update profile. Please try again.']);
    }
  };

  const handleSingleSave = async (key: string) => {
    if (key === 'uid' || key === 'accountType') return;

    try {
      await dispatch(
        updateUser({
          uid: userProfile.uid,
          updates: { [key]: editValue },
        }),
      );
      setEditingField(null);
    } catch (error) {
      console.error('Error updating profile:', error);
      // Could add visual error feedback here if needed
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold">
          {fullName}{' '}
          <span className="text-gray-500">
            (
            <Link
              to={`/${userProfile.userName}`}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {userProfile.userName}
            </Link>
            )
          </span>
        </h1>
        <SaveAndRefresh />
      </div>

      <div className="space-y-4">
        {editableFields.map(({ key, label, value, type, isArray }) => (
          <div
            key={key}
            className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative"
          >
            {editingField === key ? (
              <div className="space-y-2">
                <label className="font-medium">{label}:</label>
                {isArray ? (
                  <div className="space-y-2">
                    {arrayInputs.map((input, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type={type || 'text'}
                          value={input}
                          onChange={(e) => {
                            const newInputs = [...arrayInputs];
                            newInputs[index] = e.target.value;
                            setArrayInputs(newInputs);
                            // Clear error when user starts typing
                            if (inputErrors[index]) {
                              const newErrors = [...inputErrors];
                              newErrors[index] = '';
                              setInputErrors(newErrors);
                            }
                          }}
                          className={`flex-1 px-2 py-1 rounded border ${
                            inputErrors[index] ? 'border-red-500' : 'border-gray-300'
                          } dark:bg-zinc-700 dark:text-white`}
                        />
                        <button
                          onClick={() => {
                            const newInputs = arrayInputs.filter((_, i) => i !== index);
                            setArrayInputs(newInputs.length ? newInputs : ['']);
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <span className="sr-only">Remove</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                    {inputErrors[arrayInputs.length - 1] && (
                      <div className="text-red-500 text-sm">
                        {inputErrors[arrayInputs.length - 1]}
                      </div>
                    )}
                    <div className="flex justify-between items-center mt-2">
                      <button
                        onClick={() => setArrayInputs([...arrayInputs, ''])}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + Add another {label.toLowerCase()}
                      </button>
                      <div className="space-x-2">
                        <button
                          onClick={() => handleArraySave(key)}
                          className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingField(null);
                            setArrayInputs([]);
                            setInputErrors([]);
                          }}
                          className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <input
                    type={type || 'text'}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleSingleSave(key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSingleSave(key);
                      if (e.key === 'Escape') setEditingField(null);
                    }}
                    className="flex-1 px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white"
                    autoFocus
                  />
                )}
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="w-32 font-medium">{label}:</span>
                  <span>{value || 'Not set'}</span>
                </div>
                {isEditable(key) && (
                  <AddEditDeleteBar1
                    isVisible={true}
                    showAdd={false}
                    showDelete={false}
                    showInfo={false}
                    onEdit={() => {
                      if (isArray) {
                        handleArrayEdit(
                          key,
                          (userProfile[key as keyof typeof userProfile] as string[]) || [],
                        );
                      } else {
                        setEditingField(key);
                        setEditValue(value);
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
